import { BET_SLIP_ERRORS } from '../../constants/betSlipErrors';
import { acceptOddChangeEnum, EVENT_TYPE_ENUM, ticketTypeEnum } from '../../constants/enums';
import {
    BET_PLACEMENT_SUBMIT_BUTTON_TYPES,
    BET_SLIP_TABS,
    MAX_BET_LINE_COUNT,
    STAKE_VALUES,
    MINIMUM_ODD_VALUE,
} from '../../constants/defaultValues';
import {
    ADD_BET_SLIP_BET_LINE,
    ADD_TICKET_BET_SLIP,
    BET_SLIP_COMBINATIONS_CHANGED,
    BET_SLIP_COUPON_CHANGED,
    BET_SLIP_ERROR_TIMEOUT_CHANGED,
    BET_SLIP_EVENTS_LOADED,
    BET_SLIP_EVENTS_LOADING,
    BET_SLIP_LOADING_CHANGED,
    BET_SLIP_MAX_STAKE_LOADING,
    BET_SLIP_PLACED_TICKET_CHANGED,
    BET_SLIP_REQUEST_ID_CHANGED,
    BET_SLIP_RETRY_COUNTER_CHANGED,
    BET_SLIP_STAKES_CHANGED,
    BET_SLIP_SUBMIT_BUTTON_CHANGED,
    CHANGE_BET_SLIP_ACTIVE_TAB,
    CHANGE_BET_SLIP_BET_LINE_STAKE,
    CHANGE_BET_SLIP_DISABLED_TABS,
    CHANGE_BET_SLIP_ODD_CHANGE_DECISION,
    CHANGE_BET_STAKE_LIMITS,
    CLEAR_BET_SLIP,
    CLEAR_BET_SLIP_PLACE_ERRORS,
    DELETE_BET_SLIP_BET_LINE,
    PLACE_BET_FAILED,
    PLACE_BET_REQUEST,
    RESET_BET_SLIP_DISABLED_TABS,
    SET_BET_BANCO,
    UPDATE_BET_SLIP_BET_LINES,
} from '../actions/types';
import betSlipCalculator from '../../utils/betSlipCalculator';
import { formatMessage } from '../../utils/translation';
import { isOnlyESport, isESportBetLine } from '../../utils/helpers';

const initialDisabledTabs = [BET_SLIP_TABS.combo.id, BET_SLIP_TABS.system.id];
const initialStakes = {
    [BET_SLIP_TABS.single.id]: STAKE_VALUES.min,
    [BET_SLIP_TABS.combo.id]: STAKE_VALUES.min,
    [BET_SLIP_TABS.system.id]: STAKE_VALUES.min,
};
const initialValidations = {
    single: [],
    combo: [],
    system: [],
    common: [],
};

const initialCouponData = {
    couponBarcode: null,
    isMobile: null,
};

const initialState = {
    activeTab: BET_SLIP_TABS.single.id,
    disabledTabs: [...initialDisabledTabs],
    selectedOddChangeDecision: acceptOddChangeEnum.YES,
    betLines: [],
    stakes: { ...initialStakes },
    isFetchingEvents: false,
    isTicketSubmitting: false,
    placedTicketDetails: null,
    validations: { ...initialValidations },
    placedBetErrors: [],
    selectedCombinations: [],
    stakeLimits: {
        sportMinimum: STAKE_VALUES.min,
        eSportMinimum: STAKE_VALUES.min,
    },
    submitButtonType: BET_PLACEMENT_SUBMIT_BUTTON_TYPES.submit,
    retryCounter: 0,
    errorTimeout: false,
    couponData: initialCouponData,
    isMaxStakeLoading: false,
    requestId: null,
};

export default function (state = initialState, action) {
    const { type, payload } = action;

    switch (type) {
        case CHANGE_BET_SLIP_ACTIVE_TAB: {
            const newValidations = validateBetLines({
                betLines: state.betLines,
                activeTab: payload,
                validations: state.validations,
                selectedCombinations: [],
                stakes: state.stakes,
                stakeLimits: state.stakeLimits,
                isMobile: state.couponData.isMobile,
            });
            let newBetlines = state.betLines;

            if (payload !== BET_SLIP_TABS.system.id) {
                newBetlines = newBetlines.map(betLine => ({ ...betLine, banco: false }));
            }

            return {
                ...state,
                activeTab: payload,
                validations: newValidations,
                selectedCombinations: [],
                betLines: newBetlines,
                placedBetErrors: [],
            };
        }
        case CHANGE_BET_SLIP_DISABLED_TABS: {
            return {
                ...state,
                disabledTabs: payload,
            };
        }
        case RESET_BET_SLIP_DISABLED_TABS: {
            return {
                ...state,
                disabledTabs: [...initialDisabledTabs],
            };
        }
        case CHANGE_BET_SLIP_ODD_CHANGE_DECISION: {
            const newValidations = validateBetLines({
                betLines: state.betLines,
                activeTab: state.activeTab,
                validations: state.validations,
                selectedCombinations: state.selectedCombinations,
                stakes: state.stakes,
                stakeLimits: state.stakeLimits,
                hasOddsChanged: false,
                isMobile: state.couponData.isMobile,
            });

            return {
                ...state,
                selectedOddChangeDecision: payload,
                validations: newValidations,
            };
        }
        case ADD_TICKET_BET_SLIP: {
            const newDisabledTabs = getDisabledTabs({ betLines: payload.betLines });

            const newValidations = validateBetLines({
                betLines: payload.betLines,
                activeTab: payload.activeTab,
                validations: state.validations,
                selectedCombinations: payload.systemCombinations,
                stakes: payload.stakes,
                stakeLimits: state.stakeLimits,
                isMobile: state.couponData.isMobile,
            });

            return {
                ...state,
                betLines: payload.betLines,
                stakes: payload.stakes,
                disabledTabs: newDisabledTabs,
                activeTab: payload.activeTab,
                validations: newValidations,
                selectedCombinations: payload.systemCombinations,
                couponData: initialCouponData,
            };
        }
        case ADD_BET_SLIP_BET_LINE: {
            const { eventId, marketId, outcomeId } = payload;
            const found = state.betLines.find(betLine => {
                return (
                    betLine.eventId === eventId &&
                    betLine.marketId === marketId &&
                    betLine.outcomeId === outcomeId
                );
            });
            if (found) return state;

            const newBetLines = [...state.betLines, payload];
            const newDisabledTabs = getDisabledTabs({ betLines: newBetLines });
            const newActiveTab = getActiveTab({
                betLines: newBetLines,
                activeTab: state.activeTab,
            });

            let newValidations = validateBetLines({
                betLines: newBetLines,
                activeTab: newActiveTab,
                validations: state.validations,
                selectedCombinations: [],
                stakes: state.stakes,
                stakeLimits: state.stakeLimits,
                isMobile: state.couponData.isMobile,
            });

            return {
                ...state,
                betLines: newBetLines,
                disabledTabs: newDisabledTabs,
                activeTab: newActiveTab,
                validations: newValidations,
                selectedCombinations: [],
                couponData: initialCouponData,
            };
        }
        case DELETE_BET_SLIP_BET_LINE: {
            const { eventId, marketId, outcomeId } = payload;
            const newBetLines = state.betLines.filter(
                betLine =>
                    !(
                        betLine.eventId === eventId &&
                        betLine.marketId === marketId &&
                        betLine.outcomeId === outcomeId
                    ),
            );

            if (newBetLines.length === 0) {
                return {
                    ...state,
                    ...getInitialBetSlipState(state),
                };
            }

            const newDisabledTabs = getDisabledTabs({ betLines: newBetLines });
            const newActiveTab = getActiveTab({
                betLines: newBetLines,
                activeTab: state.activeTab,
            });

            const newValidations = validateBetLines({
                betLines: newBetLines,
                activeTab: newActiveTab,
                validations: state.validations,
                selectedCombinations: [],
                stakes: state.stakes,
                stakeLimits: state.stakeLimits,
                isMobile: state.couponData.isMobile,
            });

            return {
                ...state,
                betLines: newBetLines,
                disabledTabs: newDisabledTabs,
                activeTab: newActiveTab,
                validations: newValidations,
                selectedCombinations: [],
                couponData: initialCouponData,
            };
        }
        case UPDATE_BET_SLIP_BET_LINES: {
            let newValidations = validateBetLines({
                betLines: payload,
                activeTab: state.activeTab,
                validations: state.validations,
                selectedCombinations: state.selectedCombinations,
                stakes: state.stakes,
                stakeLimits: state.stakeLimits,
                isMobile: state.couponData.isMobile,
            });

            let newSelectedCombinations = state.selectedCombinations;

            const systems = betSlipCalculator.generateSystems(
                payload,
                state.stakes[ticketTypeEnum.SYSTEM],
            );

            if (newValidations.common.length > 0 || systems.length === 0) {
                newSelectedCombinations = [];

                newValidations = validateBetLines({
                    betLines: payload,
                    activeTab: state.activeTab,
                    validations: newValidations,
                    selectedCombinations: newSelectedCombinations,
                    stakes: state.stakes,
                    stakeLimits: state.stakeLimits,
                    isMobile: state.couponData.isMobile,
                });
            }

            return {
                ...state,
                betLines: payload,
                validations: newValidations,
                selectedCombinations: newSelectedCombinations,
            };
        }
        case CHANGE_BET_SLIP_BET_LINE_STAKE: {
            const { eventId, marketId, outcomeId, stake } = payload;
            const newBetLines = state.betLines.map(betLine => {
                if (
                    betLine.eventId === eventId &&
                    betLine.marketId === marketId &&
                    betLine.outcomeId === outcomeId
                ) {
                    return { ...betLine, stake };
                }

                return betLine;
            });

            let newValidations = validateBetLines({
                betLines: newBetLines,
                activeTab: state.activeTab,
                validations: state.validations,
                selectedCombinations: state.selectedCombinations,
                stakes: state.stakes,
                stakeLimits: state.stakeLimits,
                isMobile: state.couponData.isMobile,
            });

            return {
                ...state,
                betLines: newBetLines,
                validations: newValidations,
            };
        }
        case BET_SLIP_STAKES_CHANGED: {
            const newStakes = { ...state.stakes, [payload.type]: payload.stake };

            let newValidations = validateBetLines({
                betLines: state.betLines,
                activeTab: state.activeTab,
                validations: state.validations,
                selectedCombinations: state.selectedCombinations,
                stakes: newStakes,
                stakeLimits: state.stakeLimits,
                isMobile: state.couponData.isMobile,
            });

            return {
                ...state,
                stakes: newStakes,
                validations: newValidations,
            };
        }
        case BET_SLIP_EVENTS_LOADING: {
            return { ...state, isFetchingEvents: payload };
        }
        case BET_SLIP_EVENTS_LOADED: {
            const newBetLines = state.betLines.map(betLine => {
                const id = `${betLine.marketId}${betLine.outcomeId}`;
                const payloadEvent = payload[id];

                if (payloadEvent) {
                    return {
                        ...betLine,
                        ...payloadEvent,
                        isLive: payloadEvent.live,
                        mbn: payloadEvent.mbn || betLine.mbn,
                        changeType: '',
                    };
                } else {
                    return {
                        ...betLine,
                        changeType: '',
                        closed: true,
                        removed: true,
                    };
                }
            });

            const newValidations = validateBetLines({
                betLines: newBetLines,
                activeTab: state.activeTab,
                validations: state.validations,
                selectedCombinations: state.selectedCombinations,
                stakes: state.stakes,
                stakeLimits: state.stakeLimits,
                isMobile: state.couponData.isMobile,
            });

            return {
                ...state,
                isFetchingEvents: false,
                betLines: newBetLines,
                validations: newValidations,
            };
        }
        case PLACE_BET_REQUEST: {
            return {
                ...state,
                isTicketSubmitting: true,
                placedBetErrors: [],
            };
        }
        case PLACE_BET_FAILED:
            return {
                ...state,
                isTicketSubmitting: false,
                placedBetErrors: payload,
            };
        case BET_SLIP_PLACED_TICKET_CHANGED: {
            return {
                ...state,
                placedTicketDetails: payload,
                isTicketSubmitting: false,
            };
        }
        case BET_SLIP_LOADING_CHANGED: {
            return {
                ...state,
                isTicketSubmitting: payload,
            };
        }
        case CLEAR_BET_SLIP_PLACE_ERRORS: {
            return {
                ...state,
                placedBetErrors: [],
            };
        }
        case BET_SLIP_COMBINATIONS_CHANGED: {
            const newValidations = validateBetLines({
                betLines: state.betLines,
                activeTab: state.activeTab,
                validations: state.validations,
                selectedCombinations: payload,
                stakes: state.stakes,
                stakeLimits: state.stakeLimits,
                isMobile: state.couponData.isMobile,
            });

            return {
                ...state,
                selectedCombinations: payload,
                validations: newValidations,
            };
        }
        case SET_BET_BANCO: {
            const { eventId, marketId, outcomeId } = payload;
            const newBetLines = state.betLines.map(betLine => {
                if (
                    betLine.eventId === eventId &&
                    betLine.marketId === marketId &&
                    betLine.outcomeId === outcomeId
                ) {
                    return { ...betLine, banco: !betLine.banco };
                }

                return betLine;
            });

            const newValidations = validateBetLines({
                betLines: newBetLines,
                activeTab: state.activeTab,
                validations: state.validations,
                selectedCombinations: state.selectedCombinations,
                stakes: state.stakes,
                stakeLimits: state.stakeLimits,
                isMobile: state.couponData.isMobile,
            });

            return {
                ...state,
                betLines: newBetLines,
                validations: newValidations,
                selectedCombinations: [],
            };
        }
        case CHANGE_BET_STAKE_LIMITS: {
            const stakeLimits = {
                ...state.stakeLimits,
                ...payload,
            };
            const stakes = {
                [BET_SLIP_TABS.single.id]: payload.sportMinimum || STAKE_VALUES.min,
                [BET_SLIP_TABS.combo.id]: payload.sportMinimum || STAKE_VALUES.min,
                [BET_SLIP_TABS.system.id]: payload.sportMinimum || STAKE_VALUES.min,
            };

            const newValidations = validateBetLines({
                betLines: state.betLines,
                activeTab: state.activeTab,
                validations: state.validations,
                selectedCombinations: state.selectedCombinations,
                stakes,
                stakeLimits,
                isMobile: state.couponData.isMobile,
            });

            return {
                ...state,
                stakeLimits,
                stakes,
                validations: newValidations,
            };
        }
        case BET_SLIP_COUPON_CHANGED: {
            const { betLines, activeTab, selectedCombinations, stakes, hasOddsChanged } = payload;
            const newDisabledTabs = getDisabledTabs({ betLines: betLines });

            let newValidations = validateBetLines({
                betLines,
                activeTab,
                validations: state.validations,
                selectedCombinations,
                stakes,
                stakeLimits: state.stakeLimits,
                hasOddsChanged,
                isMobile: payload.isMobile,
            });

            return {
                ...state,
                isFetchingEvents: false,
                betLines,
                disabledTabs: newDisabledTabs,
                activeTab,
                validations: newValidations,
                selectedOddChangeDecision: payload.selectedOddChangeDecision,
                selectedCombinations: payload.selectedCombinations,
                couponData: {
                    couponBarcode: payload.couponBarcode,
                    isMobile: payload.isMobile,
                },
                stakes,
            };
        }
        case CLEAR_BET_SLIP:
            return {
                ...state,
                ...getInitialBetSlipState(state),
            };
        case BET_SLIP_SUBMIT_BUTTON_CHANGED:
            return {
                ...state,
                submitButtonType: payload,
            };
        case BET_SLIP_RETRY_COUNTER_CHANGED:
            return {
                ...state,
                retryCounter: payload,
            };
        case BET_SLIP_ERROR_TIMEOUT_CHANGED:
            return {
                ...state,
                errorTimeout: payload,
            };
        case BET_SLIP_MAX_STAKE_LOADING:
            return {
                ...state,
                isMaxStakeLoading: payload,
            };
        case BET_SLIP_REQUEST_ID_CHANGED:
            return {
                ...state,
                requestId: payload,
            };
        default:
            return state;
    }
}

const getInitialBetSlipState = state => {
    return {
        betLines: [],
        activeTab: BET_SLIP_TABS.single.id,
        disabledTabs: [...initialDisabledTabs],
        selectedOddChangeDecision: acceptOddChangeEnum.YES,
        stakes: {
            [BET_SLIP_TABS.single.id]: state.stakeLimits.sportMinimum || STAKE_VALUES.min,
            [BET_SLIP_TABS.combo.id]: state.stakeLimits.sportMinimum || STAKE_VALUES.min,
            [BET_SLIP_TABS.system.id]: state.stakeLimits.sportMinimum || STAKE_VALUES.min,
        },
        isFetchingEvents: false,
        isTicketSubmitting: false,
        placedTicketDetails: null,
        placedBetErrors: [],
        validations: { ...initialValidations },
        selectedCombinations: [],
        couponData: initialCouponData,
    };
};

const validateBetLines = ({
    betLines,
    activeTab,
    validations,
    selectedCombinations = [],
    stakes,
    stakeLimits,
    hasOddsChanged,
    isMobile,
}) => {
    let newValidations = { ...validations };

    if (betLines.length === 0) {
        return { ...initialValidations };
    }

    const addValidation = (validations, newValidation) => {
        const isAlreadyAdded = validations.find(v => v.key === newValidation.key);

        if (isAlreadyAdded) return validations;

        return [...validations, newValidation];
    };

    const deleteValidation = (validations, key) => {
        return validations.filter(v => v.key !== key);
    };

    const validateSingle = ({ mbns, validations, mbnValidation }) => {
        const hasHigherMbnThanOne = mbns.some(mbn => mbn !== 1);

        let newValidations = [...validations];

        if (hasHigherMbnThanOne) {
            newValidations = addValidation(newValidations, mbnValidation);
        } else {
            newValidations = deleteValidation(newValidations, mbnValidation.key);
        }

        return newValidations;
    };

    const validateCombo = ({ mbns, validations, mbnValidation }) => {
        const hasOneMbn = mbns.some(mbn => mbn === 1);

        let newValidations = [...validations];

        if (!hasOneMbn) {
            const maxMbn = Math.max(...mbns);

            if (mbns.length < maxMbn) {
                newValidations = addValidation(newValidations, mbnValidation);
            } else {
                newValidations = deleteValidation(newValidations, mbnValidation.key);
            }
        } else {
            newValidations = deleteValidation(newValidations, mbnValidation.key);
        }

        return newValidations;
    };

    const validateStake = ({ hasInvalidMinimumStake, validations, invalidStakeAmounts }) => {
        const minimumStakeError = { ...BET_SLIP_ERRORS.minimumStakeError };
        minimumStakeError.message = formatMessage(minimumStakeError.message, {
            amount: invalidStakeAmounts.minimum,
        });

        if (hasInvalidMinimumStake && !isMobile) {
            validations = addValidation(validations, minimumStakeError);
        } else {
            validations = deleteValidation(validations, minimumStakeError.key);
        }

        return validations;
    };

    // const validateMaxWin = ({ validations, toWin }) => {
    //     if (toWin > 250000) {
    //         return addValidation(validations, BET_SLIP_ERRORS.maximumWinError);
    //     } else {
    //         return deleteValidation(validations, BET_SLIP_ERRORS.maximumWinError.key);
    //     }
    // };

    const validateMinOdd = ({ validations, hasInvalidMinimumOdd }) => {
        if (hasInvalidMinimumOdd) {
            return addValidation(validations, BET_SLIP_ERRORS.minimumOddError);
        } else {
            return deleteValidation(validations, BET_SLIP_ERRORS.minimumOddError.key);
        }
    };

    const validateMinTotalOdd = ({ validations, hasInvalidMinimumTotalOdd }) => {
        if (hasInvalidMinimumTotalOdd) {
            return addValidation(validations, BET_SLIP_ERRORS.minimumTotalOddError);
        } else {
            return deleteValidation(validations, BET_SLIP_ERRORS.minimumTotalOddError.key);
        }
    };

    const validateCombinations = ({ validations, betLines, shouldCheckCombination }) => {
        const regularBetLines = [];
        const outrightBetLines = [];

        betLines.forEach(betLine => {
            if (betLine.eventType === EVENT_TYPE_ENUM.regular) {
                regularBetLines.push(betLine);
            } else {
                outrightBetLines.push(betLine);
            }
        });

        const uniqueRegularBetLines = new Set(regularBetLines.map(b => b.eventId));
        const uniqueOutrightBetLines = new Set(outrightBetLines.map(b => b.marketId));

        if (
            shouldCheckCombination &&
            (regularBetLines.length !== uniqueRegularBetLines.size ||
                outrightBetLines.length !== uniqueOutrightBetLines.size)
        ) {
            validations.common = addValidation(
                validations.common,
                BET_SLIP_ERRORS.combinationError,
            );
        } else {
            validations.common = deleteValidation(
                validations.common,
                BET_SLIP_ERRORS.combinationError.key,
            );
        }
    };

    const validateEventTypes = ({ validations, betLines, shouldCheckCombination }) => {
        let eventTypeCount = 0;

        if (betLines.some(b => isESportBetLine(b))) eventTypeCount++;
        if (betLines.some(b => !isESportBetLine(b) && b.eventType === EVENT_TYPE_ENUM.regular))
            eventTypeCount++;
        if (betLines.some(b => b.eventType === EVENT_TYPE_ENUM.outright)) eventTypeCount++;

        if (shouldCheckCombination && eventTypeCount > 1) {
            validations.common = addValidation(
                validations.common,
                BET_SLIP_ERRORS.eventTypeCombinationError,
            );
        } else {
            validations.common = deleteValidation(
                validations.common,
                BET_SLIP_ERRORS.eventTypeCombinationError.key,
            );
        }
    };

    const { totalStake, totalOdds, toWin, allSystemCombinationOdds } =
        betSlipCalculator.calculateTicketDetails({
            betLines,
            activeTab,
            stakes,
            selectedCombinations,
        });

    const mbns = betLines.map(b => b.mbn);

    const shouldCheckCombination = Math.max(...mbns) > 1 || activeTab !== BET_SLIP_TABS.single.id;

    validateCombinations({ validations: newValidations, betLines, shouldCheckCombination });
    validateEventTypes({ validations: newValidations, betLines, shouldCheckCombination });

    const hasInvalidMinimumOdd = betLines.some(betLine => betLine.odd < MINIMUM_ODD_VALUE);
    newValidations.common = validateMinOdd({
        validations: newValidations.common,
        hasInvalidMinimumOdd,
    });

    const invalidStakeAmounts = {
        minimum: null,
    };

    if (activeTab === BET_SLIP_TABS.single.id) {
        newValidations.single = validateSingle({
            mbns,
            validations: newValidations.single,
            mbnValidation: BET_SLIP_ERRORS.singleMbnError,
        });

        const hasInvalidMinimumStake = betLines.some(betLine => {
            if (isESportBetLine(betLine)) {
                if (betLine.stake < stakeLimits.eSportMinimum) {
                    invalidStakeAmounts.minimum = stakeLimits.eSportMinimum;
                    return true;
                }
            } else {
                if (betLine.stake < stakeLimits.sportMinimum) {
                    invalidStakeAmounts.minimum = stakeLimits.sportMinimum;
                    return true;
                }
            }
            return false;
        });

        newValidations.single = validateStake({
            hasInvalidMinimumStake,
            validations: newValidations.single,
            invalidStakeAmounts,
        });
        // newValidations.single = validateMaxWin({
        //     validations: newValidations.single,
        //     toWin,
        // });

        newValidations.combo = [];
        newValidations.system = [];
    } else if (activeTab === BET_SLIP_TABS.combo.id) {
        newValidations.single = [];
        newValidations.combo = validateCombo({
            mbns,
            validations: newValidations.combo,
            mbnValidation: BET_SLIP_ERRORS.comboMbnError,
        });

        let hasInvalidMinimumStake = false;

        if (isOnlyESport(betLines)) {
            if (stakes[BET_SLIP_TABS.combo.id] < stakeLimits.eSportMinimum) {
                invalidStakeAmounts.minimum = stakeLimits.eSportMinimum;
                hasInvalidMinimumStake = true;
            }
        } else {
            if (stakes[BET_SLIP_TABS.combo.id] < stakeLimits.sportMinimum) {
                invalidStakeAmounts.minimum = stakeLimits.sportMinimum;
                hasInvalidMinimumStake = true;
            }
        }

        newValidations.combo = validateStake({
            hasInvalidMinimumStake,
            validations: newValidations.combo,
            invalidStakeAmounts,
        });
        // newValidations.combo = validateMaxWin({
        //     validations: newValidations.combo,
        //     toWin,
        // });

        newValidations.combo = validateMinTotalOdd({
            validations: newValidations.combo,
            hasInvalidMinimumTotalOdd: totalOdds < MINIMUM_ODD_VALUE,
        });

        newValidations.system = [];
    } else if (activeTab === BET_SLIP_TABS.system.id) {
        if (selectedCombinations.length > 0 && selectedCombinations.includes(2)) {
            const mbns = betLines.filter(b => !b.banco).map(b => b.mbn);

            const combinations = [];

            for (let i = 0; i < mbns.length; i++) {
                for (let j = i + 1; j < mbns.length; j++) {
                    combinations.push([mbns[i], mbns[j]]);
                }
            }

            const hasInvalidCombination = combinations.some(combination => {
                const validatedCombination = validateCombo({
                    mbns: combination,
                    validations: newValidations.system,
                    mbnValidation: BET_SLIP_ERRORS.mbnCombinationsError,
                });

                return !!validatedCombination.find(
                    c => c.key === BET_SLIP_ERRORS.mbnCombinationsError.key,
                );
            });

            if (hasInvalidCombination) {
                newValidations.system = addValidation(
                    newValidations.system,
                    BET_SLIP_ERRORS.mbnCombinationsError,
                );
            } else {
                newValidations.system = deleteValidation(
                    newValidations.system,
                    BET_SLIP_ERRORS.mbnCombinationsError.key,
                );
            }
        } else {
            newValidations.system = deleteValidation(
                newValidations.system,
                BET_SLIP_ERRORS.mbnCombinationsError.key,
            );
        }

        let hasInvalidMinimumStake = false;

        if (totalStake > 0) {
            if (isOnlyESport(betLines)) {
                if (totalStake < stakeLimits.eSportMinimum) {
                    invalidStakeAmounts.minimum = stakeLimits.eSportMinimum;
                    hasInvalidMinimumStake = true;
                }
            } else {
                if (totalStake < stakeLimits.sportMinimum) {
                    invalidStakeAmounts.minimum = stakeLimits.sportMinimum;
                    hasInvalidMinimumStake = true;
                }
            }
        }

        newValidations.single = [];
        newValidations.combo = [];
        newValidations.system = validateStake({
            hasInvalidMinimumStake,
            validations: newValidations.system,
            invalidStakeAmounts,
        });
        newValidations.system = validateMinTotalOdd({
            validations: newValidations.system,
            hasInvalidMinimumTotalOdd: Math.min(allSystemCombinationOdds) < MINIMUM_ODD_VALUE,
        });
        // newValidations.system = validateMaxWin({
        //     validations: newValidations.system,
        //     toWin,
        // });
    }

    if (betLines.length > MAX_BET_LINE_COUNT) {
        const maxBetLineCountError = { ...BET_SLIP_ERRORS.maximumBetLineCountError };
        maxBetLineCountError.message = formatMessage(maxBetLineCountError.message, {
            count: MAX_BET_LINE_COUNT,
        });
        newValidations.common = addValidation(newValidations.common, maxBetLineCountError);
    } else {
        newValidations.common = deleteValidation(
            newValidations.common,
            BET_SLIP_ERRORS.maximumBetLineCountError.key,
        );
    }

    if (hasOddsChanged !== undefined) {
        if (hasOddsChanged) {
            newValidations.common = addValidation(
                newValidations.common,
                BET_SLIP_ERRORS.oddChangeWarning,
            );
        } else {
            newValidations.common = deleteValidation(
                newValidations.common,
                BET_SLIP_ERRORS.oddChangeWarning.key,
            );
        }
    }

    return newValidations;
};

const getDisabledTabs = ({ betLines }) => {
    const mbns = betLines.map(b => b.mbn);
    const disabledTabs = [];

    if (mbns.length < 3) {
        disabledTabs.push(BET_SLIP_TABS.system.id);
    }

    if (mbns.length < 2) {
        disabledTabs.push(BET_SLIP_TABS.combo.id);
    }

    if (mbns.find(mbn => mbn !== 1) && mbns.length > 1) {
        disabledTabs.push(BET_SLIP_TABS.single.id);
    }

    return disabledTabs;
};

const getActiveTab = ({ betLines, activeTab }) => {
    const mbns = betLines.map(b => b.mbn);
    let newActiveTab = activeTab;

    if (activeTab === BET_SLIP_TABS.single.id && mbns.length > 1) {
        newActiveTab = BET_SLIP_TABS.combo.id;
    } else if (activeTab === BET_SLIP_TABS.combo.id && mbns.length < 2) {
        newActiveTab = BET_SLIP_TABS.single.id;
    } else if (activeTab === BET_SLIP_TABS.system.id && mbns.length < 3) {
        newActiveTab = BET_SLIP_TABS.combo.id;
    }

    return newActiveTab;
};
